import { CounterUsage } from "./counterUsage";

/** Which of the two fuel counters a figure came from. */
export type FuelSource = "vehicle" | "telematics";

export interface ChosenFuelCounter {
  usage: CounterUsage;
  /** `null` when neither counter answered with anything. */
  source: FuelSource | null;
}

/**
 * Picks the fuel counter to report from.
 *
 * Geotab keeps two of them. `DiagnosticTotalFuelUsedId` is what the vehicle itself puts on its
 * engine bus; `DiagnosticDeviceTotalFuelId` is what the telematics device works out when the
 * vehicle does not report a figure of its own. Which one a vehicle populates depends on its make,
 * its model year and how the harness is wired, and plenty of vehicles populate only one — so
 * asking for a single one of them is a reason for the report to show no consumption for a vehicle
 * that is in fact reporting perfectly well.
 *
 * The vehicle figure wins when there is one, being measured rather than derived.
 *
 * @param vehicle - Usage from the engine bus counter.
 * @param telematics - Usage from the telematics device counter.
 * @returns The counter to report from, and which one it is.
 */
export function chooseFuelCounter(
  vehicle: CounterUsage,
  telematics: CounterUsage,
): ChosenFuelCounter {
  if (vehicle.used != null) return { usage: vehicle, source: "vehicle" };
  if (telematics.used != null) return { usage: telematics, source: "telematics" };

  // Neither gave a usable figure. Report from whichever said more, so that "one reading" is
  // distinguished from "nothing at all" in the row.
  if (vehicle.sampleCount >= telematics.sampleCount) {
    return { usage: vehicle, source: vehicle.sampleCount > 0 ? "vehicle" : null };
  }
  return { usage: telematics, source: "telematics" };
}
